import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { FileUploader } from "react-drag-drop-files";
import { FormProvider, useController, useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import { DocumentPreviewDialog } from "../../components/CommonComponents/DialogConfirmation/DocPreviewDialog";
import {
  DeleteButton,
  DocDecisionButton,
  DownloadButton,
  PaymentStatusButton,
  ToggleButton,
  UploadButton,
  ViewButton,
} from "../../components/CommonComponents/EditComponent/EditComponent";
import Input from "../../components/CommonComponents/Input/Input";
import { CertificateAreaField } from "../../components/CommonComponents/CertificateFields/CertificateAreaField";
import { NavbarApplicationInfo } from "../../components/CommonComponents/Navbar/NavbarApplicationInfo";
import Select from "../../components/CommonComponents/Select/Select";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { ErrorToast } from "../../components/toastMessage";
import { BASE_URL } from "../../constants/Constants";
import { branchFeatureFlags } from "../../config/branchFeatureFlags";
import { isPhase2Continuation, resolveDocumentLabel } from "../../config/phaseConstants";
import APP_CONFIG from "../../config/appConfig";
import {
  useMoujaConfigs,
  getKhasaraRangeForMouja,
  splitCombinedKhasaraNo,
} from "../../hooks/mouja-khasara";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
import { isLevel2 } from "../../hoc/isLevel2";
import {
  TApplication,
  useServiceCertificateConditionField,
  useNameOfServiceField,
  useApplicationLogs,
  useApplicationFormSteps,
  TPopulatedForm,
} from "../../hooks/applications.hooks";
import {
  TApplicationField,
  TApplicationFieldOption,
} from "../../hooks/application-fields.hooks";
import { useToggle } from "../../hooks/common";
import {
  TSavedDemandNoteData,
  useApplicationForLevel2,
  useLevel2AllReviews,
  useLevel2LastReview,
  useLevel2DecisionMaps,
  useLevel2UpdateDocumentDecision,
  useLevel2UpdateNocDecision,
} from "../../hooks/level-2-application-approval.hooks";
import {
  TCertificateData,
  TLevel2PaymentReceipt,
  useApplicationReceiptsForLevel2,
  useDemandNoteData,
  useGenerateCertificateForLevel2,
  useLevel2CertificateData,
  useLevel2LastPaymentReview,
  useLevel2PaymentRejectApplication,
  useLevel2PaymentReturnApplication,
  useLevel2SaveCertificateData,
  useLevel2UpdateReceiptStatus,
  useSaveLevel2PaymentReview,
} from "../../hooks/level-2-payment-approval.hooks";
import { useAutoFillDemandNoteData } from "../../hooks/level-3-application-approval.hooks";
import uploadImage, { uploadPlotShape } from "../../utils/azure-storage-blob";
import { downloadResource } from "../../utils/common";
import { formatDate } from "../../utils/date";
import style from "../superAdmin/logo/logo.module.css";
import { RenderAllReviews } from "./RenderAllReviews";
import SelectDirection, { availableDirections } from "./SelectDirection";
import SelectPlotShape from "./SelectPlotShape";
import { _axios } from "../../utils/axios";
import { RenderAllLogs } from "./RenderLogs";
import SchematicPlanPage from "./SchematicPlanPage";

export const LevelTwoPaymentApproval = isLevel2(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const [screen, setScreen] = useState<
    "review" | "generate-doc" | "schematic-plan" | "upload-map"
  >("review");

  const id = +location.state?.id;

  const {
    isError: isError1,
    isFetching: isFetching1,
    data: application,
  } = useApplicationForLevel2({ id });

  const receiptsQuery = useApplicationReceiptsForLevel2({ id });
  const demandNoteDataQuery = useDemandNoteData({ applicationId: id });

  const decisionMapQuery = useLevel2DecisionMaps({ id });
  const formStepsResult = useApplicationFormSteps(
    Number(application?.name_of_service)
  );
  const steps = formStepsResult?.data?.data || [];

  const autoFill = useAutoFillDemandNoteData();

  const serviceQuery = useNameOfServiceField();
  const nameOfService = serviceQuery.data?.options?.length
    ? serviceQuery.data.options.find(
      (e) => e?.id === application?.name_of_service
    )
    : "";

  const condition = useServiceCertificateConditionField({
    name_of_service: (nameOfService as TApplicationFieldOption)?.name,
  });
  console.log("name", nameOfService);

  const isFetching =
    isFetching1 ||
    receiptsQuery.isLoading ||
    demandNoteDataQuery.isLoading ||
    decisionMapQuery.isLoading ||
    formStepsResult.isFetching;

  const receipts = receiptsQuery.data;
  const demandNoteData = demandNoteDataQuery.data;

  const { documentDecisions, nocDecisions } = decisionMapQuery.data || {};

  const isPhase2 = isPhase2Continuation(application);

  const isError =
    isError1 ||
    decisionMapQuery.isError ||
    formStepsResult.isError ||
    (!isPhase2 && (demandNoteDataQuery.isError || receiptsQuery.isError)) ||
    (!isFetching &&
      (!application || (!isPhase2 && (!receipts?.length || !demandNoteData?.id))));

  const setStuff = useSetStuffToRenderOnNavbar();

  useEffect(() => {
    setStuff(
      <NavbarApplicationInfo
        applicationId={application?.applicationId}
        status={application?.status}
        applicationType={application?.rl_application_type}
        currentPhase={application?.current_phase}
      />
    );
    return () => setStuff(null);
  }, [setStuff, application]);

  if (isNaN(id)) {
    return <Navigate to={"/user-applications"} replace />;
  }

  if (
    isFetching ||
    isError ||
    !application ||
    (!isPhase2 && (!demandNoteData || !receipts?.length))
  ) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  let toRender = null;

  switch (screen) {
    case "review": {
      // Phase 2 continuation apps were already reviewed one screen up
      // (LevelTwoApplicationViewDocsAndConfirm.tsx) and never have a separate
      // payment/demand-note record, so skip this duplicate review screen.
      toRender = isPhase2 ? (
        <GenerateCertificateWrapper
          application={application}
          autoFill={autoFill}
          condition={condition?.data?.[0] || {}}
          onBack={() => setScreen("review")}
          onForward={() => {
            if (branchFeatureFlags.ENABLE_L2_UPLOAD_MAP_SCREEN) {
              setScreen("upload-map");
            } else {
              navigate("/level-2-applications");
            }
          }}
        />
      ) : (
        <Review
          receipts={receipts!}
          application={application}
          demandNote={demandNoteData!}
          onNext={() => setScreen("schematic-plan")}
          allDocsApproved={
            // BEFORE (kept for reference per Rule 11): checked every
            // receipt ever returned by the applicant, including old,
            // permanently-superseded (is_current = 0) rows — an old
            // receipt's frozen "Payment Discrepancy" record made this
            // stay false forever, disabling Approve even once the
            // current receipt was Acknowledged.
            // receiptsQuery.data?.every((e) => e?.payment_status !== "Payment Discrepancy")
            !receiptsQuery.data?.filter((e: any) => Number(e?.is_current) === 1).length ||
            receiptsQuery.data
              ?.filter((e: any) => Number(e?.is_current) === 1)
              .every((e) => e?.payment_status !== "Payment Discrepancy")
          }
          steps={steps}
          documentDecisions={documentDecisions}
          nocDecisions={nocDecisions}
        />
      );
      break;
    }
    case "generate-doc": {
      toRender = (
        <GenerateCertificateWrapper
          application={application}
          autoFill={autoFill}
          condition={condition?.data?.[0] || {}}
          onBack={() => setScreen("schematic-plan")}
          onForward={() => {
            if (branchFeatureFlags.ENABLE_L2_UPLOAD_MAP_SCREEN) {
              setScreen("upload-map");
            } else {
              navigate("/level-2-applications");
            }
          }}
        />
      );
      break;
    }
    case "schematic-plan": {
      toRender = (
        <SchematicPlanPage
          application={application}
          onBack={() => setScreen("review")}
          onForward={() => setScreen("generate-doc")}
        />
      );
      break;
    }
    case "upload-map": {
      toRender = (
        <UploadMapWrapper
          application={application}
          onBack={() => setScreen("schematic-plan")}
          onForward={() => { }}
        />
      );
      break;
    }
  }

  return (
    <>
      <div className="px-2 pb-2 sm:p-4">
        {/* <div className="bg-white p-4 sm:p-8 rounded-2xl">{toRender}</div> */}
        <div
          className={`${style.IFrameOutCss} bg-white p-4 sm:p-8 rounded-xl shadow-lg border border-gray-300`}
        >
          {toRender}
        </div>
      </div>
    </>
  );
});

const openInNewTab = (url: string) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

const simpleDownload = (url: string) => {
  window.open(url, "_blank");
};

type GenerateCertificateProps = {
  onBack: () => unknown;
  application: TApplication & Record<string, unknown>;
  autoFill?: any;
  condition?: any;
  onForward: () => unknown;
};

export function GenerateCertificateWrapper({
  application,
  autoFill,
  condition,
  onBack,
  onForward,
}: GenerateCertificateProps) {
  const isPhase2 = isPhase2Continuation(application);

  const lastPaymentReviewQuery = useLevel2LastPaymentReview({
    id: !isPhase2 ? application.id : undefined,
  });
  const lastDocReviewQuery = useLevel2LastReview({
    id: isPhase2 ? application.id : undefined,
  });

  const reviewId = isPhase2
    ? lastDocReviewQuery.data?.review?.id
    : lastPaymentReviewQuery.data?.id;

  const activeReviewQuery = isPhase2
    ? lastDocReviewQuery
    : lastPaymentReviewQuery;

  const savedCertificateDataQuery = useLevel2CertificateData({
    applicationId: application.id,
    reviewId: reviewId,
  });

  const isFetching =
    activeReviewQuery.isFetching ||
    (!!reviewId && savedCertificateDataQuery.isFetching);
  const isError =
    activeReviewQuery.isError ||
    (!!reviewId && savedCertificateDataQuery.isError);

  if (isFetching || !reviewId) {
    return (
      <div className="flex flex-col justify-center items-center p-8 space-y-3">
        <SpinnerCircular />
        <p className="text-gray-600 font-semibold">Loading certificate generator...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center p-8">
        <p className="text-red-500 font-semibold">Something went wrong while fetching info!</p>
      </div>
    );
  }

  return (
    <GenerateCertificate
      reviewId={reviewId}
      application={application}
      autoFill={autoFill}
      condition={condition}
      onBack={onBack}
      onForward={onForward}
      defaultValues={savedCertificateDataQuery.data ?? undefined}
    />
  );
}

function GenerateCertificate({
  defaultValues,
  application,
  autoFill,
  condition,
  onBack,
  reviewId,
  onForward,
}: GenerateCertificateProps & {
  defaultValues?: TCertificateData;
  reviewId: number;
}) {
  const navigate = useNavigate();
  const [open, toggleSelectShape] = useToggle();
  const [openDirection, toggleDirection] = useToggle();
  const [pdfToView, setPdfToView] = useState<null | string>(null);
  const [documents, setDocuments] = useState([]);
  const forwardMutation = useGenerateCertificateForLevel2();
  const saveDataMutation = useLevel2SaveCertificateData();

  console.log("application", application);
  // Inside GenerateCertificate function
  const [isEditable, setIsEditable] = useState({
    applicantName: false,
    layoutName: false,
    mauja: false,
    khasaraNo: false,
    plotNo: false,
  });

  const toggleEditable = (field: keyof typeof isEditable) => {
    setIsEditable((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  useEffect(() => {
    _axios
      .get(`${BASE_URL}/api/level-three-documents/doclist/${application.id}`)
      .then(function (res) {
        setDocuments(res.data.data);
      })
      .catch(function (error) {
        // handle error
        console.log(error);
      });
  }, []);
  const formMethods = useForm({
    defaultValues: {
      // ab: 0,
      // bc: 0,
      // cd: 0,
      // da: 0,
      builtUpArea: 0,
      regularizedArea: 0,
      /** Deduction in Plot Area, entered manually by the reviewer. */
      deductionInPlotArea: 0,
      demandNoteAmountPaid: 0,
      plotAreaAsPerSaleDeed: 0,
      areaToSurrenderToNagarParishad: 0,
      areaOfConstructionToBeDemolished: 0,
      applicantName: application.plot_owner_s_name || "",
      applicationNumber: application?.applicationId || "",
      constructionType: application.construction_type || "",
      layoutName: application.unauthorized_layout_name || "",
      mauja: application.mauja || "",
      plotNo: application.plot_no || "",
      ...defaultValues,
      applicationSubmissionDate:
        defaultValues?.applicationSubmissionDate || application?.submittedAt
          ? dayjs(
            defaultValues?.applicationSubmissionDate ||
            application?.submittedAt
          ).format("YYYY-MM-DD")
          : null,
      khasaraNo: application?.khasara_no || "",
      main_khasara_no:
        defaultValues?.main_khasara_no ||
        splitCombinedKhasaraNo(application?.khasara_no).main,
      manual_khasara_no:
        defaultValues?.manual_khasara_no ||
        splitCombinedKhasaraNo(application?.khasara_no).manual,
      municipality: autoFill?.data?.[0]?.municipal_council_name || "",
      tehsil: autoFill?.data?.[0]?.tahasil_name || "",
      district: autoFill?.data?.[0]?.district_name || "",
      logo: autoFill?.data?.[0]?.certificate_logo || "",
      headerText: autoFill?.data?.[0]?.certificate_header_text || "",
      conditions: condition?.status === 1 ? condition?.condition : "",
      schematicDimension:
        sessionStorage.getItem(`schematic_${application.applicationId}`) ||
        defaultValues?.schematicDimension,
    },
    mode: "onBlur",
    shouldFocusError: true,
  });

  const { formState, watch, setValue } = formMethods;
  const moujaConfigs = useMoujaConfigs();
  const selectedMauja = watch("mauja");
  const { minLimit, maxLimit, maujaDisplayName, khasaraOptions } =
    getKhasaraRangeForMouja(moujaConfigs, selectedMauja);

  const enableBahaduraCertFields = Boolean(
    (APP_CONFIG as any)?.ENABLE_BAHADURA_CERTIFICATE_FIELDS
  );
  const isPermanentCertificate =
    (application as any)?.rl_application_type === "Permanent" ||
    isPhase2Continuation(application);
  const isTemporaryCert = !isPermanentCertificate;
  const lockCertFields = Boolean(
    (APP_CONFIG as any)?.ENABLE_BAHADURA_CERTIFICATE_LOCK
  );
  const hasConstruction =
    Boolean(application?.is_there_a_construction) ||
    String(application?.is_there_a_construction) === "1" ||
    String(application?.is_there_a_construction).toLowerCase() === "true" ||
    String(application?.is_there_a_construction).toLowerCase() === "yes";

  const handleKhasaraCombine = (mainVal: string, manualVal: string) => {
    const cleanManual = (manualVal || "").trim();
    setValue("manual_khasara_no", cleanManual);
    setValue("main_khasara_no", mainVal);
    if (mainVal && cleanManual) {
      setValue("khasaraNo", `${mainVal}/${cleanManual}`, { shouldValidate: true });
    } else if (mainVal) {
      setValue("khasaraNo", mainVal, { shouldValidate: true });
    } else if (cleanManual) {
      setValue("khasaraNo", cleanManual, { shouldValidate: true });
    }
  };

  const schematicDimension = useController({
    control: formMethods.control,
    name: "schematicDimension",
    defaultValue: defaultValues?.schematicDimension,
    // rules: { required: "* Required" },
  });

  const plotShape = useController({
    control: formMethods.control,
    name: "plotShape",
    defaultValue: defaultValues?.plotShape,
    // rules: { required: "* Required" },
  });

  const direction = useController({
    control: formMethods.control,
    name: "direction",
    defaultValue: defaultValues?.direction,
    // rules: { required: "* Required" },
  });

  useEffect(() => {
    const stored =
      defaultValues?.schematicDimension ||
      sessionStorage.getItem(`schematic_${application.applicationId}`);

    if (stored) {
      schematicDimension.field.onChange(stored);
    }
  }, [application.applicationId, defaultValues?.schematicDimension]);

  const [isEditableConditions, setIsEditableConditions] = useState(false);
  const toggleEditableConditions = () => {
    setIsEditableConditions((prev) => !prev);
  };

  return (
    <FormProvider {...formMethods}>
      <h4
        className={`text-center text-grey-600 font-semibold mb-8 text-2xl ${pdfToView ? "hidden" : ""
          }`}
      >
        Certificate Fields
      </h4>

      <form
        id="create-certificate"
        className={`grid grid-cols-2 gap-4 ${pdfToView ? "hidden" : ""}`}
        noValidate
        onSubmit={formMethods.handleSubmit((data) => {
          const finalSchematic =
            data.schematicDimension ||
            defaultValues?.schematicDimension ||
            sessionStorage.getItem(`schematic_${application.applicationId}`) ||
            "";

          saveDataMutation.mutate(
            {
              applicationId: application.id,
              reviewId,
              data: {
                ...data,
                schematicDimension: finalSchematic,
              },
            },
            {
              onSuccess: (result) => {
                const blob = result.data;

                if (!(blob instanceof Blob)) {
                  console.error("Not a Blob:", blob);
                  ErrorToast("Invalid PDF received from server");
                  return;
                }

                const url = URL.createObjectURL(blob);
                setPdfToView(url);
              },
            }
          );
        })}
      >
        {/* Applicant Name */}
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center justify-between">
            <label className="text-base font-bold">Applicant Name</label>
            <i
                className={`fa ${isEditable.applicantName ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                onClick={() => toggleEditable("applicantName")}
            ></i>
          </div>
          <Input
              disabled={!isEditable.applicantName}
              placeholder="Applicant Name"
              className={!isEditable.applicantName ? "bg-gray-100" : "bg-white"}
              {...formMethods.register("applicantName")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Application Number"
            placeholder="Application Number"
            {...formMethods.register("applicationNumber")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            type="date"
            label="Application Submission Date"
            placeholder="Application Submission Date"
            {...formMethods.register("applicationSubmissionDate", {
              valueAsDate: true,
            })}
          />
        </div>
        {/* Layout Name */}
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center justify-between">
            <label className="text-base font-bold">Name of Layout to be regularized</label>
            {!lockCertFields && (
              <i
                className={`fa ${isEditable.layoutName ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                onClick={() => toggleEditable("layoutName")}
              ></i>
            )}
          </div>
          <Input
            disabled={lockCertFields || !isEditable.layoutName}
            placeholder="Name of Layout to be regularized"
            className={lockCertFields || !isEditable.layoutName ? "bg-gray-100" : "bg-white"}
            {...formMethods.register("layoutName")}
          />
        </div>
        {/* Layout Holder Name (Temporary + Permanent, Bahadura cert flag) */}
        {enableBahaduraCertFields && (
          <div className="col-span-2 md:col-span-1">
            <Input
              label="Layout Holder Name"
              placeholder="Layout Holder Name"
              {...formMethods.register("layoutHolderName")}
            />
          </div>
        )}
        {/* Layout Holder Address (Temporary RL only) */}
        {enableBahaduraCertFields && isTemporaryCert && (
          <div className="col-span-2 md:col-span-1">
            <Input
              label="Layout Holder Address"
              placeholder="Layout Holder Address"
              {...formMethods.register("layoutHolderAddress")}
            />
          </div>
        )}
        {/* Ownership/Registered Document No & Date (Permanent RL only) */}
        {enableBahaduraCertFields && !isTemporaryCert && (
          <>
            <div className="col-span-2 md:col-span-1">
              <Input
                label="Ownership/Registered Document No"
                placeholder="Ownership/Registered Document No"
                {...formMethods.register("ownershipDocumentNo")}
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <Input
                type="date"
                label="Ownership/Registered Document Date"
                placeholder="Ownership/Registered Document Date"
                {...formMethods.register("ownershipDocumentDate", { valueAsDate: true })}
              />
            </div>
          </>
        )}
        <div className="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Mauza (Top-Left on Desktop, 1st on Mobile) */}
          <div className="order-1">
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold text-gray-700">Mauza</label>
              {!lockCertFields && (
                <i
                  className={`fa ${isEditable.mauja ? "fa-lock" : "fa-pencil"} cursor-pointer text-xs text-gray-600`}
                  onClick={() => toggleEditable("mauja")}
                ></i>
              )}
            </div>
            <Select
              disabled={lockCertFields || !isEditable.mauja}
              className={lockCertFields || !isEditable.mauja ? "bg-gray-100" : "bg-white"}
              {...formMethods.register("mauja")}
              value={(watch("mauja") as string) || ""}
            >
              <option value="" disabled hidden>
                Select Mouza
              </option>
              {Array.from(
                new Set([
                  ...moujaConfigs.map((m) => m.mouja_name).filter(Boolean),
                  ...(selectedMauja &&
                  !moujaConfigs.some((m) => m.mouja_name === selectedMauja)
                    ? [selectedMauja as string]
                    : []),
                ])
              ).map((name) => (
                <option key={`l2cert-mauja-${name}`} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </div>

          {/* Main Khasara No (Top-Right on Desktop, 2nd on Mobile) */}
          <div className="order-2">
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold text-gray-700">Khasara No</label>
              {!lockCertFields && (
                <i
                  className={`fa ${isEditable.khasaraNo ? "fa-lock" : "fa-pencil"} cursor-pointer text-xs text-gray-600`}
                  onClick={() => toggleEditable("khasaraNo")}
                ></i>
              )}
            </div>
            <Select
              disabled={lockCertFields || !isEditable.khasaraNo || !selectedMauja}
              helperText={!selectedMauja ? "Please select Mouja first" : ""}
              className={lockCertFields || !isEditable.khasaraNo ? "bg-gray-100" : "bg-white"}
              value={watch("main_khasara_no") || ""}
              onChange={(e: any) => {
                handleKhasaraCombine(e.target.value, watch("manual_khasara_no"));
              }}
            >
              <option value="" disabled hidden>
                {selectedMauja
                  ? `Select Khasara (${minLimit} to ${maxLimit})`
                  : "Please select Mouja first"}
              </option>
              {khasaraOptions.map((num) => (
                <option key={`l2cert-khasara-${num}`} value={String(num)}>
                  Khasara No. {num} ({maujaDisplayName})
                </option>
              ))}
            </Select>
          </div>

          {/* Plot No (Bottom-Left on Desktop, 4th on Mobile) */}
          <div className="order-4 md:order-3">
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold text-gray-700">Plot No</label>
              {!lockCertFields && (
                <i
                  className={`fa ${isEditable.plotNo ? "fa-lock" : "fa-pencil"} cursor-pointer text-xs text-gray-600`}
                  onClick={() => toggleEditable("plotNo")}
                  title={isEditable.plotNo ? "Lock Field" : "Edit Field"}
                ></i>
              )}
            </div>
            <Input
              disabled={lockCertFields || !isEditable.plotNo}
              placeholder="Plot No"
              className={lockCertFields || !isEditable.plotNo ? "bg-gray-100" : "bg-white"}
              {...formMethods.register("plotNo")}
            />
          </div>

          {/* Sub-Khasara No. (Bottom-Right on Desktop below Khasara No, 3rd on Mobile below Khasara No) */}
          <div className="order-3 md:order-4">
            <Input
              label="Sub-Khasara No."
              placeholder="Manual Sub-Khasara (e.g. 55/65)"
              disabled={lockCertFields || !isEditable.khasaraNo}
              className={lockCertFields || !isEditable.khasaraNo ? "bg-gray-100" : "bg-white"}
              value={watch("manual_khasara_no") || ""}
              onChange={(e) =>
                handleKhasaraCombine(watch("main_khasara_no"), e.target.value)
              }
            />
          </div>
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();

              e.stopPropagation();

              setTimeout(() => {
                e.currentTarget.focus();
              }, 0);
            }}
            label="Demand Note Amount Paid"
            placeholder="Demand Note Amount Paid"
            {...formMethods.register("demandNoteAmountPaid", {
              valueAsNumber: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            label="Receipt Number"
            placeholder="Receipt Number"
            {...formMethods.register("receiptNumber")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            type="date"
            label="Payment Receipt Date"
            placeholder="Payment Receipt Date"
            {...formMethods.register("paymentReceiptDate", {
              valueAsDate: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            label="Use for which the plot is regularized"
            placeholder="Use for which the plot is regularized"
            {...formMethods.register("constructionType")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();

              e.stopPropagation();

              setTimeout(() => {
                e.currentTarget.focus();
              }, 0);
            }}
            label="Plot Area As Per Sale Deed [sq. mt.]"
            placeholder="Plot Area As Per Sale Deed [sq. mt.]"
            {...formMethods.register("plotAreaAsPerSaleDeed", {
              valueAsNumber: true,
            })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();

              e.stopPropagation();

              setTimeout(() => {
                e.currentTarget.focus();
              }, 0);
            }}
            label="Regularized Area [sq. mt.]"
            placeholder="Regularized Area [sq. mt.]"
            {...formMethods.register("regularizedArea", {
              valueAsNumber: true,
            })}
          />
        </div>
        {/**
         * Deduction in Plot Area, shared with L1 and L3 via
         * CertificateAreaField rather than copying this block a third time.
         */}
        <CertificateAreaField
          label="Deduction in Plot Area [sq. mt.]"
          name="deductionInPlotArea"
          register={formMethods.register}
        />
        {Boolean((APP_CONFIG as any)?.ENABLE_CONSTRUCTION_QUESTION) && hasConstruction && (
          <div className="col-span-2 md:col-span-1">
            <Input
              type="number"
              onWheel={(e) => {
                e.currentTarget.blur();
                e.stopPropagation();
                setTimeout(() => {
                  e.currentTarget.focus();
                }, 0);
              }}
              label="Built-up Area Regularized [sq. mt.]"
              placeholder="Built-up Area Regularized [sq. mt.]"
              {...formMethods.register("builtUpArea", {
                valueAsNumber: true,
              })}
            />
          </div>
        )}
        <div className="col-span-2 md:col-span-1">
          <Input
            label="Address"
            placeholder="Address"
            {...formMethods.register("address")}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            required
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();
              e.stopPropagation();
              setTimeout(() => {
                e.currentTarget.focus();
              }, 0);
            }}
            label="Plot Area To Be Surrendered To Nagar Parishad"
            placeholder="Plot Area To Be Surrendered To Nagar Parishad"
            error={!!formState.errors?.areaToSurrenderToNagarParishad}
            helperText={formState?.errors?.areaToSurrenderToNagarParishad?.message?.toString()}
            {...formMethods.register("areaToSurrenderToNagarParishad", {
              required: "* Required",
              valueAsNumber: true,
            })}
          />
        </div>
        {Boolean((APP_CONFIG as any)?.ENABLE_CONSTRUCTION_QUESTION) && hasConstruction && (
          <div className="col-span-2 md:col-span-1">
            <Input
              required
              type="number"
              onWheel={(e) => {
                e.currentTarget.blur();
                e.stopPropagation();
                setTimeout(() => {
                  e.currentTarget.focus();
                }, 0);
              }}
              label="Area Of Construction To Be Demolished"
              placeholder="Area Of Construction To Be Demolished"
              error={!!formState.errors?.areaOfConstructionToBeDemolished}
              helperText={formState?.errors?.areaOfConstructionToBeDemolished?.message?.toString()}
              {...formMethods.register("areaOfConstructionToBeDemolished", {
                required: "* Required",
                valueAsNumber: true,
              })}
            />
          </div>
        )}
        <div className="col-span-2 md:col-span-1">
          <Input
            label="Permit No"
            placeholder="Permit No"
            {...formMethods.register("permitNo")}
          />
        </div>
        <div className="col-span-2 md:col-span-2">
          {/* div for balancing UI */}
        </div>
        {/*
        <div className="col-span-2 md:col-span-1">
          {schematicDimension.field.value && (
            <div className="mt-2">
              <label className="text-base font-bold mb-2">
                Schematic Dimension
              </label>
              <img
                className="rounded-lg border border-gray-200"
                src={String(schematicDimension.field.value)}
                width={150}
                alt="Schematic Dimension"
              />
            </div>
          )}
        </div>
        */}
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Municipal Council Name"
            placeholder="Municipal Council Name"
            error={!!formState.errors?.municipality}
            helperText={formState?.errors?.municipality?.message?.toString()}
            {...formMethods.register("municipality", {
              required: "* Required",
            })}
          />
        </div>

        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Office Address: Tehsil"
            placeholder="Office Address: Tehsil"
            error={!!formState.errors?.tehsil}
            helperText={formState?.errors?.tehsil?.message?.toString()}
            {...formMethods.register("tehsil", { required: "* Required" })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            disabled
            label="Office Address: District"
            placeholder="Office Address: District"
            error={!!formState.errors?.district}
            helperText={formState?.errors?.district?.message?.toString()}
            {...formMethods.register("district", { required: "* Required" })}
          />
        </div>
        <div className="col-span-2 md:col-span-2">
          {/* div for balancing UI */}
        </div>
        <div className="col-span-2 md:col-span-1 pt-2">
          <label className="text-base font-bold mb-2">Copy To</label>
          <textarea
            placeholder="Copy To"
            {...formMethods.register("copyTo")}
            rows={5}
          />
        </div>
        <div className="col-span-2 md:col-span-1 pt-2">
          <label className="text-base font-bold mb-2">ULC Comment</label>
          <textarea
            placeholder="ULC Comment"
            {...formMethods.register("ulcComment")}
            rows={5}
          />
        </div>
        <div className="col-span-2">
          <label className="flex items-center">
            <span className="text-base font-bold mb-2">
              Conditions For Certificate
            </span>
            {!lockCertFields && (
              <i
                className={`fa ${isEditableConditions ? "fa-lock" : "fa-pencil"
                  } cursor-pointer ml-2`}
                onClick={toggleEditableConditions}
              ></i>
            )}
          </label>
          <textarea
            placeholder="Conditions for Certificate"
            {...formMethods.register("conditions")}
            rows={5}
            className={`h-auto w-100 ${lockCertFields || !isEditableConditions ? "bg-gray-200" : "bg-white"
              }`}
            disabled={lockCertFields || !isEditableConditions}
          />
        </div>
        <div className="col-span-2 flex-wrap md:flex-nowrap flex justify-center gap-4 items-center mt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={saveDataMutation.isPending || forwardMutation.isPending}
            className="bg-black px-5 md:px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
          >
            Back
          </button>
          <button
            form="create-certificate"
            type="submit"
            disabled={saveDataMutation.isPending || forwardMutation.isPending}
            className="bg-primary px-5 md:px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
          >
            {"Save And Preview"}
          </button>
        </div>
      </form>
      {
        !!pdfToView && (
          <>
            {/* <iframe
            title="PDF Viewer"
            className="w-full h-full min-h-screen"
            src={pdfToView + "#toolbar=0"}
          /> */}
            <iframe
              title="PDF Viewer"
              // className="w-full h-full min-h-screen hidden sm:block"
              className={style.IFrameCss}
              src={pdfToView + "#toolbar=0"}
            />
            <div className="hidden">
              <p>Certificate</p>
              <a
                className="inline-block text-white py-2 px-4 rounded-full bg-blue-500"
                download={`${application.applicationId}-Certificate.pdf`}
                href={pdfToView}
              >
                Download
              </a>
            </div>
            <div className="col-span-2 flex justify-center space-x-4 items-center mt-8">
              <button
                type="button"
                onClick={() => setPdfToView(null)}
                disabled={saveDataMutation.isPending || forwardMutation.isPending}
                className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => {
                  forwardMutation.mutate(
                    {
                      applicationId: application.id,
                      reviewId,
                    },
                    {
                      onSuccess: () => {
                        onForward();
                      },
                    }
                  );
                }}
                disabled={saveDataMutation.isPending || forwardMutation.isPending}
                className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
              >
                {forwardMutation.isPending ? "Forwarding..." : "Forward to Level 3 Reviewer"}
              </button>
            </div>
          </>
        )
      }
      {
        open && (
          <SelectPlotShape
            onClose={toggleSelectShape}
            selected={plotShape.field.value || ""}
            onSelect={(n) => {
              plotShape.field.onChange(n);
              toggleSelectShape();
            }}
          />
        )
      }
      {
        openDirection && (
          <SelectDirection
            onClose={toggleDirection}
            selected={direction.field.value || ""}
            onSelect={(n) => {
              direction.field.onChange(n);
              toggleDirection();
            }}
          />
        )
      }
    </FormProvider >
  );
}
function UploadCertificateWrapper({
  application,
  onBack,
  onUpload,
}: {
  application: TApplication & Record<string, unknown>;
  onBack: () => unknown;
  onUpload: () => unknown;
}) {
  const fileTypes = ["JPG", "PNG", "GIF", "PDF"];
  const [file, setFile] = useState<File | null>(null);
  const [isModal, setIsModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleFile = (e: File) => {
    setFile(e);
  };

  const onUploadAndForward = async () => {
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("application_id", application.applicationId.toString());
      formData.append("doc_type", "certificate");
      formData.append("submitted_by", "Level 2");

      const response = await _axios.post(
        "/level-2-payment-approval/upload-demand-note",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success === true) {
        onUpload();
      } else {
        throw new Error(response.data.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      ErrorToast(`Upload failed: ${error}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="w-full">
      <h2 className="text-2xl mb-10">
        Upload Certificate After Digital Signature
      </h2>
      <div
        className="flex flex-col md:flex-row"
        style={{ paddingBottom: "20px" }}
      >
        <FileUploader
          handleChange={handleFile}
          name="file"
          types={fileTypes}
          children={
            <div className={style.singleUpload}>
              <div className="flex rounded-md border-inherit border p-2">
                <button className="px-7 py-2 text-white bg-yellow-500 rounded-lg">
                  Browse
                </button>
                <small className={" pl-4 self-center"}>
                  Drag and Drop file here
                </small>
              </div>
            </div>
          }
        />
        {file && (
          <div
            className="bg-slate-100 rounded-lg py-3 px-3 mx-0 md:mx-4 mt-4 md:mt-0 self-center h-65 md:h-65"
            style={{ height: "400px", overflow: "hidden" }}
            onClick={() => setIsModal(true)}
          >
            {file?.type !== "application/pdf" ? (
              <img
                src={URL.createObjectURL(file)}
                alt="Logo"
                className={`${style.cloudUpload} w-full`}
              />
            ) : (
              <div style={{ height: "370px", width: "280px" }}>
                <Worker
                  workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
                >
                  <Viewer fileUrl={URL.createObjectURL(file)} />
                </Worker>
              </div>
            )}
          </div>
        )}
        {file && (
          <div className="inline-block">
            <DeleteButton onClick={() => setFile(null)} />
          </div>
        )}
      </div>
      <div className="col-span-2 flex justify-center space-x-4 items-center mt-8">
        <button
          type="button"
          onClick={onBack}
          disabled={isUploading}
          className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onUploadAndForward}
          disabled={!file || isUploading}
          className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
        >
          {isUploading ? "Uploading..." : "Upload and Forward"}
        </button>
      </div>

      <DocumentPreviewDialog onClose={() => setIsModal(false)} open={isModal}>
        {file && (
          <Worker
            workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
          >
            <Viewer fileUrl={URL.createObjectURL(file)} />
          </Worker>
        )}
      </DocumentPreviewDialog>
    </div>
  );
}



export function UploadMapWrapper({
  application,
  onBack,
}: GenerateCertificateProps & {
  defaultValues?: TCertificateData;
}) {
  const fileTypes = ["JPG", "PNG", "GIF", "PDF"];
  const navigate = useNavigate();
  const [files, setFiles] = useState<{ [key: number]: File }>({});
  const [isModal, setIsModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>();
  const [documents, setDocuments] = useState<any>([]);
  const [isUploading, setIsUploading] = useState(false);
  const lastReviewQuery = useLevel2LastPaymentReview({ id: application.id });
  const [previewDocument, setPreviewDocument] = useState<{
    url: string;
  } | null>(null);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);

  useEffect(() => {
    _axios
      .get(
        `${BASE_URL}/api/level-three-documents/doclistpermitable/:userId/${application.id}`
      )
      .then(function (res) {
        setDocuments(res.data.data);
        setDocumentsLoaded(true);
      })
      .catch(function (error) {
        console.log(error);
        setDocumentsLoaded(true);
      });
  }, []);

  const reviewId = lastReviewQuery.data?.id;
  const savedCertificateDataQuery = useLevel2CertificateData({
    applicationId: application.id,
    reviewId: reviewId,
  });
  const forwardMutation = useGenerateCertificateForLevel2();

  const handleFile = (e: File, index: number) => {
    setFiles((prevFiles) => {
      const newFiles = { ...prevFiles, [index]: e };
      console.log(`File uploaded for index ${index}:`, e.name);
      return newFiles;
    });
  };

  const isFetching =
    lastReviewQuery.isFetching || savedCertificateDataQuery.isFetching;
  const isError =
    lastReviewQuery.isError || savedCertificateDataQuery.isError || !reviewId;

  if (isFetching || isError) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  // Updated upload function using your level-2-payment backend
  const onUploadAndForward = async () => {
    if (Object.keys(files).length !== documents?.length) {
      ErrorToast("Please Sign all documents");
      return;
    }

    setIsUploading(true);
    try {
      // Create FormData for multiple file upload
      const formData = new FormData();

      // Add application_id
      formData.append("application_id", application.applicationId.toString());

      // Prepare document IDs array
      const documentIds = documents.map((doc: any) => doc.id);
      formData.append("document_ids", JSON.stringify(documentIds));

      // Add all files to FormData
      documents.forEach((doc: any, index: number) => {
        const file = files[index];
        if (file) {
          formData.append("files", file);
        }
      });

      console.log("Uploading files to level-2-payment backend...");
      console.log("Document IDs:", documentIds);
      console.log("Files count:", Object.keys(files).length);

      // Upload to your level-2-payment backend endpoint
      const response = await _axios.post(
        `${BASE_URL}/api/level-2-payment-approval/upload-multiple-documents`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Backend upload response:", response.data);

      if (response.data.success === true) {
        console.log("Files uploaded successfully, now forwarding...");

        // Forward the application after successful upload
        forwardMutation.mutate(
          { applicationId: application.id, reviewId },
          {
            onSuccess: () => {
              console.log("Application forwarded successfully");
              navigate("/level-2-applications");
            },
            onError: (error) => {
              console.error("Forward failed:", error);
              ErrorToast(
                "Upload successful but forwarding failed. Please try again."
              );
            },
          }
        );
      } else {
        throw new Error(response.data.message || "Upload failed");
      }
    } catch (error) {
      console.error("Error in onUploadAndForward:", error);
      ErrorToast(`Upload failed: ${error}`);
    } finally {
      setIsUploading(false);
    }
  };

  const documentColumns = [
    {
      name: "Document Name",
      cell: (d: any) => <span className="text-center">{d.document_label}</span>,
      center: true,
      grow: 3,
    },
    {
      name: "Upload",
      center: true,
      width: "300px",
      cell: (d: any, index: number) =>
        files[index] ? (
          <div className="flex flex-col items-center sm:flex-row sm:items-center">
            <UploadButton
              onClick={() => {
                setSelectedDoc({
                  file: files[index],
                });
                setIsModal(true);
              }}
            />
            <span className="text-center sm:text-left max-w-[200px] ml-2">
              {files[index].name}
            </span>
          </div>
        ) : (
          <FileUploader
            handleChange={(e: File) => handleFile(e, index)}
            name={`file-${index}`}
            types={fileTypes}
          >
            <div className={`${style.singleUpload} w-full`}>
              <div className="flex justify-center rounded-md border-inherit border p-2">
                <button className="px-4 py-2 text-white bg-yellow-500 rounded-lg text-sm sm:text-base sm:px-7">
                  Browse
                </button>
              </div>
            </div>
          </FileUploader>
        ),
    },
    {
      name: "View",
      center: true,
      grow: 0.5,
      cell: (d: any) => (
        <div className="flex space-x-4">
          <ViewButton
            // onClick={() => {
            //   console.log("View button clicked", d?.document);
            //   setPreviewDocument({ url: String(d?.document) });
            // }}
            onClick={() => {
              openInNewTab(String(d?.document));
            }}
          />
          <DownloadButton
            // onClick={() => downloadResource(String(d?.document))}
            onClick={() => simpleDownload(String(d?.document))}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      {documentsLoaded && documents.length > 0 ? (
        <>
          <h2 className="text-2xl mb-10">Upload Map After Digital Signature</h2>
          <DataTable
            keyField="url"
            className="mt-4"
            data={(documents as any) ?? []}
            customStyles={tableCustomStyles}
            columns={documentColumns}
          />
          <div className="col-span-2 flex justify-center space-x-4 items-center mt-8">
            <button
              type="button"
              onClick={onBack}
              disabled={isUploading || forwardMutation.isPending}
              className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              Back
            </button>
            <button
              type="button"
              onClick={onUploadAndForward}
              disabled={
                Object.keys(files).length !== documents?.length ||
                isUploading ||
                forwardMutation.isPending
              }
              className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              {isUploading
                ? "Uploading..."
                : forwardMutation.isPending
                  ? "Forwarding..."
                  : "Upload and Forward"}
            </button>
          </div>
        </>
      ) : documentsLoaded ? (
        <>
          <h2 className="text-2xl mb-10">Upload Map After Digital Signature</h2>
          <div className="col-span-2 flex flex-col items-center mt-8">
            <span className="text-1xl mb-10">
              There are no records to display
            </span>
            <div className="col-span-2 flex justify-center space-x-4 items-center mt-8">
              <button
                type="button"
                onClick={onBack}
                disabled={isUploading || forwardMutation.isPending}
                className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() =>
                  forwardMutation.mutate(
                    { applicationId: application.id, reviewId },
                    { onSuccess: () => navigate("/level-2-applications") }
                  )
                }
                disabled={isUploading || forwardMutation.isPending}
                className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
              >
                {forwardMutation.isPending ? "Forwarding" : "Forward"}
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col justify-center items-center p-8">
          <h2 className="text-2xl mb-10">Upload Map After Digital Signature</h2>
          <SpinnerCircular />
        </div>
      )}

      <DocumentPreviewDialog onClose={() => setIsModal(false)} open={isModal}>
        {selectedDoc && (
          <Worker
            workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
          >
            <Viewer
              fileUrl={
                selectedDoc?.document ?? URL.createObjectURL(selectedDoc?.file)
              }
            />
          </Worker>
        )}
      </DocumentPreviewDialog>

      <DocumentPreviewDialog
        onClose={() => setPreviewDocument(null)}
        open={!!previewDocument}
      >
        {previewDocument &&
          previewDocument.url &&
          (previewDocument.url.endsWith("pdf") ? (
            <Worker
              workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
            >
              <Viewer fileUrl={previewDocument.url} />
            </Worker>
          ) : (
            <img
              src={previewDocument.url}
              className="object-contain w-full h-full"
              alt="Document"
            />
          ))}
      </DocumentPreviewDialog>
    </div>
  );
}

type ReviewProps = {
  onNext: () => unknown;
  application: TApplication & Record<string, unknown>;
  allDocsApproved?: boolean;
  receipts: TLevel2PaymentReceipt[];
  demandNote: { id: number } & TSavedDemandNoteData;
  steps?: TPopulatedForm[];
  documentDecisions?: Record<string, boolean>;
  nocDecisions?: Record<string, boolean>;
};

function KeyValue({
  label,
  value,
}: {
  label: string;
  value?: string | number;
}) {
  return (
    <div className="flex items-center flex-wrap sm:flex-nowrap">
      <p className="text-grey-600 text-xs sm:text-base font-bold shrink-0 sm:basis-1/2 mr-6">
        {label}
      </p>
      <p className="text-grey-600 text-xs sm:text-base grow">{value || "--"}</p>
    </div>
  );
}

function Review({
  allDocsApproved,
  onNext,
  application,
  demandNote,
  receipts,
  steps,
  documentDecisions,
  nocDecisions,
}: ReviewProps) {
  const [document, setDocument] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const navigate = useNavigate();
  const [logsDialog, toggleLogsDialog] = useToggle();
  const [reviewsDialog, toggleReviewsDialog] = useToggle();
  const allReviews = useLevel2AllReviews({ id: application.id });
  const allLogsQuery = useApplicationLogs({ id: +application.id });
  const saveReviewMutation = useSaveLevel2PaymentReview();
  const lastReviewQuery = useLevel2LastPaymentReview({ id: application.id });
  const [comment, setComment] = useState("");
  const updateReceiptStatusMutation = useLevel2UpdateReceiptStatus();
  const rejectMutation = useLevel2PaymentRejectApplication();
  const returnMutation = useLevel2PaymentReturnApplication();

  const enableBahaduraCharges = Boolean(
    (APP_CONFIG as any)?.ENABLE_BAHADURA_PLOT_DEMAND_NOTE_CHARGES
  );

  const hasConstruction =
    Boolean(application?.is_there_a_construction) ||
    String(application?.is_there_a_construction) === "1" ||
    String(application?.is_there_a_construction).toLowerCase() === "true" ||
    String(application?.is_there_a_construction).toLowerCase() === "yes";

  const docDecisionMutation = useLevel2UpdateDocumentDecision();
  const nocDecisionMutation = useLevel2UpdateNocDecision();
  const { isPending: updatingNocDecision, mutate: updateNocDecision } =
    nocDecisionMutation;
  const { isPending: updatingDocDecision, mutate: updateDocDecision } =
    docDecisionMutation;

  const id = application.id;
  const status = application?.status;

  const disableBackBtn =
    lastReviewQuery.isFetching ||
    saveReviewMutation.isPending ||
    rejectMutation.isPending ||
    returnMutation.isPending ||
    updateReceiptStatusMutation.isPending;

  const disabled =
    disableBackBtn || application.status !== "Pending Level 2 Final Review";

  const lastReviewData = lastReviewQuery.data;

  useEffect(() => {
    setComment(lastReviewData?.comment_text || "");
  }, [lastReviewData]);

  const { mutate: updateStatus } = updateReceiptStatusMutation;

  // is_current is the sole source of truth for "active vs historical" —
  // it only changes when the applicant uploads a brand new receipt, never
  // as a side effect of a reviewer toggling payment_status. A receipt
  // already superseded by a newer upload is permanently read-only here.
  const activeReceipts = useMemo(
    () => (receipts || []).filter((r: any) => Number(r?.is_current) === 1),
    [receipts]
  );
  const historicalReceipts = useMemo(
    () => (receipts || []).filter((r: any) => Number(r?.is_current) !== 1),
    [receipts]
  );

  // ORIGINAL IMPLEMENTATION PRESERVED FOR AUDIT REFERENCE:
  /*
  const receiptsTable = useMemo(
    (): TableColumn<TLevel2PaymentReceipt>[] => [
      {
        name: "Sr. No.",
        selector: (_, i) => (i || 0) + 1,
        center: true,
        grow: 0.5,
      },
      {
        name: "Application ID",
        selector: (d) => d.application_id,
        center: true,
        grow: 2,
      },
      {
        name: "Receipt No",
        selector: (d) => String(d?.receipt_no || "-"),
        center: true,
        width: "150px",
      },
      {
        name: "Paid Amount",
        selector: (d) => (d?.amount_paid ? `${d.amount_paid} INR` : "-"),
        center: true,
        width: "160px",
      },
      {
        name: "Payment Date",
        center: true,
        cell: (d) => (d?.payment_date ? formatDate(d.payment_date) : "-"),
        width: "160px",
      },
      {
        name: "Receipt",
        center: true,
        cell: (d, i) => ( ... ),
      },
      {
        name: "Action",
        center: true,
        cell: (d, i) => ( ... ),
      },
    ],
    [disabled, updateStatus, id]
  );
  */

  // MODIFIED IMPLEMENTATION: Increased column minWidths to prevent text ellipsis truncation
  const receiptsTable = useMemo(
    (): TableColumn<TLevel2PaymentReceipt>[] => [
      {
        name: "Sr. No.",
        selector: (_, i) => (i || 0) + 1,
        center: true,
        minWidth: "120px",
      },
      {
        name: "Application ID",
        selector: (d) => d.application_id,
        center: true,
        minWidth: "160px",
        grow: 1,
      },
      {
        name: "Receipt No",
        selector: (d) => String(d?.receipt_no || "-"),
        center: true,
        minWidth: "220px",
      },
      {
        name: "Paid Amount",
        selector: (d) => (d?.amount_paid ? `${d.amount_paid} INR` : "-"),
        center: true,
        minWidth: "200px",
      },
      {
        name: "Payment Date",
        center: true,
        cell: (d) => (d?.payment_date ? formatDate(d.payment_date) : "-"),
        minWidth: "180px",
      },
      {
        name: "Receipt",
        center: true,
        minWidth: "140px",
        cell: (d, i) => (
          <div className="flex space-x-4">
            <ViewButton
              onClick={() => {
                openInNewTab(String(d?.receipt_file));
              }}
            />
            <DownloadButton
              onClick={() => simpleDownload(String(d?.receipt_file))}
            />
          </div>
        ),
      },
      {
        name: "Action",
        center: true,
        minWidth: "240px",
        cell: (d, i) => (
          <PaymentStatusButton
            paymentStatus={d.payment_status}
            disabled={disabled}
            onClick={() => {
              updateStatus({
                applicationId: id,
                receiptId: d.receipt_id,
                status:
                  d.payment_status === "Payment Discrepancy"
                    ? "Payment Acknowledgment"
                    : "Payment Discrepancy",
              });
            }}
            tooltipText="Toggle Payment Status"
          />
        ),
      },
    ],
    [disabled, updateStatus, id]
  );

  // ADD NOC COLUMNS 
  const nocColumns = useMemo((): TableColumn<Record<string, unknown>>[] => {
    const noc = steps?.find((e) => e.type === "NOC");
    if (!noc) return [];
    return [
      {
        name: "Sr. No.",
        selector: (_, i) => (i || 0) + 1,
        center: true,
        grow: 0.5,
      },
      {
        name: "NOC Type",
        selector: (d) =>
          String(
            (noc.fields?.find((e: any) => e.uid === "noc_type")?.options || []).find(
              (e: any) => e.id === d.noc_type
            )?.name || "-"
          ),
        center: true,
        width: "190px",
      },
      {
        name: "NOC No",
        selector: (d) => String(d?.noc_number || "-"),
        center: true,
        width: "180px",
      },
      {
        name: "NOC Date",
        selector: (d) => formatDate(String(d?.noc_date || "x")),
        center: true,
        width: "180px",
      },
      {
        name: "Level 1",
        center: true,
        cell: () => (
          <i className="m-0 fa fa text-lg fa-check-square-o py-2 px-3 rounded-md bg-gray-400 text-gray-600"></i>
        ),
      },
      {
        name: "View",
        center: true,
        width: "180px",
        cell: (d, i) => (
          <div className="flex space-x-4">
            <ToggleButton
              isChecked={nocDecisions?.[String(d?.id)] !== false}
              isUpdating={updatingNocDecision}
              statusCheck={status}
              onClick={() => {
                updateNocDecision({
                  applicationId: id,
                  decision: nocDecisions?.[String(d?.id)] === false ? 1 : 0,
                  nocId: Number(d.id),
                });
              }}
              tooltipText="Toggle NOC Decision"
            />
            <ViewButton
              onClick={() => {
                openInNewTab(String(d?.noc_doc));
              }}
            />
            <DownloadButton
              onClick={() => simpleDownload(String(d?.noc_doc))}
            />
          </div>
        ),
      },
    ];
  }, [status, steps, updatingNocDecision, nocDecisions, updateNocDecision, id]);

  // ADD DOCUMENT TABLE 
  const documentTable = useMemo((): {
    docs: { id: number; name: string; url: string }[];
    documentColumns: TableColumn<{
      id: number;
      name: string;
      url: string;
    }>[];
  } => {
    const docForm = steps?.find((e) => e.type === "Documents");

    if (!docForm) return { docs: [], documentColumns: [] };
    return {
      // WHAT/WHY/HOW: hit lookup now goes through the shared
      // resolveDocumentLabel (config/phaseConstants.ts) instead of a plain
      // docForm.documentConfigs.find(...), so Permanent RL Phase 2's K Prat
      // Document / Registry Certificate (document_config_id 9991/9992) no
      // longer get silently filtered out of this table — see that
      // function's header comment for the full root-cause explanation.
      docs: Object.entries(application?.documents || {})
        .map(([k, { document: url, id: documentId }]) => {
          const hit = resolveDocumentLabel(docForm, +k);

          if (!hit) return null;
          return { name: hit, url, id: documentId };
        })
        .filter((x): x is NonNullable<typeof x> => !!x),
      documentColumns: [
        {
          name: "Sr. No.",
          selector: (_, i) => (i || 0) + 1,
          center: true,
          grow: 0.5,
        },
        {
          name: "Document Name",
          center: true,
          cell: (d) => <span className="text-center">{d.name}</span>,
          grow: 3,
          width: "380px",
        },
        {
          name: "Level 1",
          center: true,
          cell: () => (
            <i className="m-0 fa fa text-lg fa-check-square-o py-2 px-3 rounded-md bg-gray-400 text-gray-600"></i>
          ),
        },
        {
          name: "View",
          center: true,
          width: "180px",
          cell: (d, i) => (
            <div className="flex space-x-4">
              <DocDecisionButton
                isChecked={documentDecisions?.[String(d?.id)] !== false}
                isUpdating={updatingDocDecision}
                statusCheck={status}
                onClick={() => {
                  updateDocDecision({
                    applicationId: id,
                    decision:
                      documentDecisions?.[String(d?.id)] === false ? 1 : 0,
                    docId: Number(d.id),
                  });
                }}
                tooltipText="Toggle Document Decision"
              />
              <ViewButton
                onClick={() => {
                  openInNewTab(String(d?.url));
                }}
              />
              <DownloadButton
                onClick={() => simpleDownload(String(d?.url))}
              />
            </div>
          ),
        },
      ],
    };
  }, [
    steps,
    status,
    application,
    updateDocDecision,
    updatingDocDecision,
    documentDecisions,
    id,
  ]);

  if (lastReviewQuery.isLoading || lastReviewQuery.isError) {
    return (
      <div className="flex justify-center items-center p-8">
        {lastReviewQuery.isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  if (logsDialog) {
    return (
      <div className="px-4 pb-4 md:p-4">
        <div className="bg-white p-3 md:p-8">
          <h4 className="text-center text-grey-600 font-semibold mb-4 text-2xl">
            Audit History
          </h4>
          <div className="flex justify-start mb-4 gap-6">
            <button
              type="button"
              className="text-base font-bold disabled:text-gray-500"
              onClick={toggleLogsDialog}
            >
              <i className="fa fa-arrow-left"></i>
              Back
            </button>
          </div>
          <RenderAllLogs
            onClose={toggleLogsDialog}
            logs={allLogsQuery.data ?? []}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div>
        <h4 className="text-2xl text-center font-bold mb-4">
          {isPhase2Continuation(application)
            ? "📜 Phase 2 Review & Permanent RL Certificate"
            : "Payment Details"}
        </h4>
        {(() => {
          const interestPositive =
            demandNote.interestAccumulated !== undefined &&
            demandNote.interestAccumulated !== null &&
            Number(demandNote.interestAccumulated) > 0;

          const items: { label: string; value?: string | number }[] = [
            { label: "Applicant Name", value: demandNote.applicantName },
            { label: "Layout Name", value: demandNote.layoutName },
            { label: "Mauza", value: demandNote.mauja },
            { label: "Khasara", value: demandNote.khasaraNo },
            {
              label: "Plot Area",
              value: demandNote.area
                ? `${demandNote.area} ${demandNote.unit_area}`
                : "-",
            },
            {
              label: "Is there a construction?",
              value: hasConstruction ? "Yes" : "No",
            },
          ];

          if (hasConstruction && Number(demandNote.constructionArea || 0) > 0) {
            items.push({
              label: "Built-up Area [sq. mt.]",
              value: demandNote.constructionArea,
            });
          }

          items.push(
            {
              label: "Due Date",
              value: demandNote.dueDate ? formatDate(demandNote.dueDate) : "",
            },
            { label: "Plot No", value: demandNote.plotNo }
          );

          if (enableBahaduraCharges) {
            items.push(
              { label: "Scrutiny Charges", value: demandNote.scrutinyCharges },
              { label: "Development Charges", value: demandNote.particular2Total },
              { label: "Compounding Charges", value: demandNote.compoundingCharges },
              { label: "Supervision Charges", value: demandNote.supervision_charges },
              { label: "SWM Charges", value: demandNote.swmCharges },
              { label: "Administrative Charges", value: demandNote.administrativeCharges },
              { label: "Survey / Measurement Charges", value: demandNote.surveyMeasurementCharges },
              { label: "Infrastructure Fee", value: demandNote.infrastructureFee },
              { label: "Total Amount", value: demandNote.baseAmount || demandNote.totalAmount }
            );
          } else {
            items.push(
              { label: "Particular 1 INR", value: demandNote.particular1Total },
              { label: "Particular 2 INR", value: demandNote.particular2Total },
              { label: "Scrutiny Charges", value: demandNote.scrutinyCharges },
              {
                label: "Total Amount of Demand INR",
                value: interestPositive
                  ? `${demandNote.baseAmount || demandNote.totalAmount} INR`
                  : demandNote.totalAmount,
              }
            );
          }

          if (interestPositive) {
            items.push(
              {
                label: "Late Payment Penalty (1.5% Compounded)",
                value: `${demandNote.interestAccumulated} INR (${demandNote.daysPending || 0} days pending)`,
              },
              {
                label: "Total Recalculated Amount of Demand",
                value: `${demandNote.totalAmount} INR`,
              }
            );
          }

          const numbered = items.map((it, i) => ({ ...it, number: i + 1 }));
          const splitAt = Math.ceil(numbered.length / 2);
          const leftItems = numbered.slice(0, splitAt);
          const rightItems = numbered.slice(splitAt);

          return (
            <div className="flex justify-between-items-center flex-wrap md:flex-nowrap mb-4 gap-4">
              <div className="basis-full md:basis-1/2 grow space-y-4">
                {leftItems.map((it) => (
                  <KeyValue key={it.number} label={`${it.number}. ${it.label}`} value={it.value} />
                ))}
              </div>
              <div className="basis-full md:basis-1/2 grow space-y-4">
                {rightItems.map((it) => (
                  <KeyValue key={it.number} label={`${it.number}. ${it.label}`} value={it.value} />
                ))}
              </div>
            </div>
          );
        })()}
        <DataTable
          data={activeReceipts}
          keyField="receipt_id"
          columns={receiptsTable}
          customStyles={tableCustomStyles}
          // className="border border-gray-300 mb-4"
          className="mb-4"
        />
        {historicalReceipts.length > 0 && (
          <div className="mb-4 pt-2 border-t border-gray-200">
            <p className="text-center text-gray-500 font-semibold mb-2 text-sm">
              Historical Returned Receipts (Read-Only)
            </p>
            <div className="space-y-2">
              {historicalReceipts.map((r: any) => (
                <div
                  key={r.receipt_id}
                  className="flex flex-wrap items-center justify-between gap-3 bg-gray-100 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-500"
                >
                  <span>Receipt No: <strong>{r.receipt_no || "-"}</strong></span>
                  <span>Amount: <strong>{r.amount_paid ? `${r.amount_paid} INR` : "-"}</strong></span>
                  <span>Date: <strong>{r.payment_date ? formatDate(r.payment_date) : "-"}</strong></span>
                  <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 text-xs font-semibold">
                    {r.payment_status || "-"}
                  </span>
                  <div className="flex gap-3">
                    <ViewButton onClick={() => openInNewTab(String(r?.receipt_file))} />
                    <DownloadButton onClick={() => simpleDownload(String(r?.receipt_file))} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* {NOC Table} */}
        {!!nocColumns?.length &&
          !!(application?.nocs as Record<string, unknown>[])?.length && (
            <div className="mb-4">
              <p className="text-grey-600 text-base font-semibold mb-2">NOC Table:</p>
              <DataTable
                keyField="url"
                className="border border-none rounded-none"
                columns={nocColumns}
                customStyles={tableCustomStyles}
                data={(application?.nocs as Record<string, unknown>[]) || []}
              />
            </div>
          )}

        {/* DOCUMENT TABLE */}
        {!!documentTable.docs?.length && (
          <div className="mb-4">
            <p className="text-grey-600 text-base font-semibold mb-2">
              Document Table:
            </p>
            <DataTable
              keyField="url"
              className="border border-none rounded-none"
              data={documentTable.docs}
              customStyles={tableCustomStyles}
              columns={documentTable.documentColumns}
            />
          </div>
        )}
        <div className="grid grid-cols-7 gap-4 md:gap-8">
          <div className="col-span-7 sm:col-span-5 lg:col-span-5">
            <div className="flex justify-between items-center mb-2 gap-4">
              <label className="text-sm md:text-base font-bold grow">
                Level 2 Reviewer Remark <span className="text-red-500">*</span>
              </label>
              {!!allLogsQuery.data?.length && (
                <button
                  type="button"
                  className="text-base font-bold disabled:text-gray-500"
                  onClick={toggleLogsDialog}
                >
                  Audit History
                </button>
              )}
              <button
                className="text-sm md:text-base font-bold disabled:text-gray-500"
                disabled={!allReviews.data?.length}
                onClick={toggleReviewsDialog}
              >
                <i className="fa fa-history"></i>
                Previous Reviews
              </button>
              {application.status !== "Complete" && (
                <button
                  className="text-blue-600 underline"
                  onClick={() =>
                    window.open("/blocked-plots-viewer", "_blank")
                  }
                >
                  List of Blocked Plots
                </button>
              )}
            </div>
            <textarea
              placeholder="Add Comment"
              value={comment}
              disabled={disabled}
              rows={5}
              className="h-auto w-100"
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
          <div className="flex sm:flex-col col-span-7 sm:col-span-2 lg:col-span-2 gap-3 mt-8">
            <button
              type="button"
              disabled={disabled || !lastReviewData?.id || !allDocsApproved}
              onClick={onNext}
              className="bg-primary py-2 text-sm md:text-base rounded-lg font-bold text-white px-3 basis-1/2 sm:basis-auto disabled:bg-gray-300 disabled:text-gray-500"
            >
              Approve & Next
            </button>
            <button
              type="button"
              disabled={
                disabled || !lastReviewQuery.data?.id || allDocsApproved
              }
              className="bg-red-400 py-2 text-sm md:text-base rounded-lg font-bold text-white px-3 basis-1/2 sm:basis-auto disabled:bg-gray-300 disabled:text-gray-500"
              onClick={() =>
                returnMutation.mutate(
                  {
                    applicationId: application.id,
                    application_id: application.applicationId,
                    user_id: application.userId,
                  },
                  { onSuccess: () => navigate("/level-2-applications") }
                )
              }
            >
              {returnMutation.isPending ? "Returning" : "Return"}
            </button>
            <button
              type="button"
              disabled={disabled || !lastReviewQuery.data?.id}
              className="bg-red-600 py-2 text-sm md:text-base rounded-lg font-bold text-white px-3 basis-1/2 sm:basis-auto disabled:bg-gray-300 disabled:text-gray-500"
              onClick={() =>
                rejectMutation.mutate(
                  {
                    applicationId: application.id,
                    application_id: application.applicationId,
                    user_id: application.userId,
                  },
                  { onSuccess: () => navigate("/level-2-applications") }
                )
              }
            >
              {rejectMutation.isPending ? "Rejecting" : "Reject"}
            </button>
          </div>
          <div className="col-span-7 flex flex-wrap md:flex-nowrap justify-center gap-4 items-center">
            <button
              type="button"
              disabled={disableBackBtn}
              onClick={() => navigate(-1)}
              className="bg-black px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              Back
            </button>
            <button
              type="button"
              disabled={disabled || !comment?.trim()}
              onClick={() => {
                saveReviewMutation.mutate({
                  applicationId: application.id,
                  comment,
                });
              }}
              className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              {saveReviewMutation.isPending ? "Saving" : "Save"}
            </button>
          </div>
        </div>
      </div>
      {reviewsDialog && (
        <RenderAllReviews
          onClose={toggleReviewsDialog}
          reviews={allReviews.data || []}
        />
      )}
      {!!document && (
        <DocumentPreviewDialog
          onClose={() => setDocument(null)}
          open={!!document}
        >
          {document.url?.toLowerCase().endsWith(".pdf") ? (
            <Worker
              workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
            >
              <Viewer fileUrl={document.url} />
            </Worker>
          ) : (
            <img
              src={document.url}
              alt={document.name || "Document"}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          )}
        </DocumentPreviewDialog>
      )}
    </>
  );
}
